import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog'
import { useAuth } from '../../contexts/AuthContext'
import { CreditCard, LogOut, Loader2, Smartphone, Copy, Check } from 'lucide-react'
import { useEffect } from 'react'
import { profileService } from '../../services/profile.service'
import { Button } from '../ui/button'
import { ConfirmPurchaseDialog } from './ConfirmPurchaseDialog'
import { CREDIT_PACKAGES, creditsToPln } from '@/config/credit-packages'
import { INVITED_CREDITS, INVITER_BONUS_CREDITS } from '@superlekcja/shared'
import { useTranslation } from 'react-i18next'
import { useToast } from '../ui/use-toast'
import { LogoutConfirmDialog } from './logout-confirm-dialog'
import { useSearchParams } from 'react-router-dom'
import { track } from '../../utils/analytics'
import { PLANS } from '@superlekcja/shared'
import { TopupOffer } from '@/survey/topup-offer'
import type { ProfileDialogOrigin } from '@/contexts/ProfileDialogProvider'

const paymentsEnabled = import.meta.env.VITE_PAYMENTS_ENABLED === 'true'

interface UserProfileDialogProps {
  isOpen: boolean
  origin: ProfileDialogOrigin | null
  onClose: () => void
  onLogout?: () => void
}

export function UserProfileDialog({ isOpen, origin, onClose, onLogout }: UserProfileDialogProps) {
  const { t } = useTranslation()
  const { profile, user, logout, refreshProfile } = useAuth()
  const { toast } = useToast()
  const [purchaseCreditsAmount, setPurchaseCreditsAmount] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()
  const [isPendingTransaction, setIsPendingTransaction] = useState(false)
  const [isBlikPayment, setIsBlikPayment] = useState(false)
  const [isCopied, setIsCopied] = useState(false)

  useEffect(() => {
    if (isOpen) {
      refreshProfile();
    }
  }, [isOpen, refreshProfile]);

  // Check if there's a pending transaction
  useEffect(() => {
    const transactionId = searchParams.get('transactionPending');
    setIsPendingTransaction(!!transactionId);
  }, [searchParams]);

  const handleBuyCredits = (credits: number) => {
    setPurchaseCreditsAmount(credits);
  };

  const handleConfirmPurchase = async (paymentType: 'blik' | 'other', blikCode?: string) => {
    if (!purchaseCreditsAmount) return;

    setIsLoading(true);
    try {
      const response = await profileService.purchaseCredits(purchaseCreditsAmount, paymentType, blikCode);

      const amount = PLANS[purchaseCreditsAmount as keyof typeof PLANS]
      track('purchase_credits', {
        success: true,
        amount,
        currency: "PLN"
      });

      if (response.redirectUrl) {
        // For standard payment methods, redirect to PayU
        window.location.href = response.redirectUrl;
      } else {
        // For BLIK payments, start polling for transaction status
        searchParams.set('transactionPending', response.extOrderId);
        setSearchParams(searchParams);
        setPurchaseCreditsAmount(null); // Close the purchase dialog
        setIsPendingTransaction(true);
        setIsBlikPayment(true);
      }
    } catch (error) {
      console.error('Failed to purchase credits:', error);
      toast({
        variant: "destructive",
        title: "Błąd",
        description: "Nie udało się rozpocząć procesu płatności. Spróbuj ponownie później.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout()
    setShowLogoutConfirm(false)
    onClose()
    onLogout?.()
  }

  const inviteLink = user?.id ? `${window.location.protocol}//${window.location.host}/?invitedBy=${encodeURIComponent(user.id)}` : null

  const handleCopyInviteLink = () => {
    if (inviteLink) {
      navigator.clipboard.writeText(inviteLink)
      setIsCopied(true)
      setTimeout(() => setIsCopied(false), 2000)
      toast({
        title: "Skopiowano link",
        description: "Link do zaproszenia został skopiowany do schowka",
      })
    }
  }

    return (
      <>
        <Dialog open={isOpen} onOpenChange={onClose}>
          <DialogContent
            data-testid="profile-modal"
            withSlideAnimation={false}
            className="sm:max-w-[425px]"
            style={{
              transformOrigin: origin
                ? `calc(${origin.x}px - 50vw + 50%) calc(${origin.y}px - 50vh + 50%)`
                : undefined,
            }}
          >
            <DialogHeader>
              <DialogTitle>Twój profil</DialogTitle>
              <DialogDescription>
                Sprawdź stan swojego konta i dostępne kredyty
              </DialogDescription>
            </DialogHeader>

            <div className="py-4">
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div className="flex items-center gap-3">
                  <CreditCard className="h-5 w-5 text-muted-foreground" />
                  <span className="text-sm font-medium">Dostępne kredyty</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-bold" data-testid="credits-display">
                    {profile?.credits ?? 0}
                  </span>
                  {isPendingTransaction && (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  )}
                </div>
              </div>

              {paymentsEnabled ? (
                <div className="mt-4 space-y-2">
                  <h3 className="text-sm font-medium text-muted-foreground">
                    {isPendingTransaction ? (
                      <div className="space-y-2">
                        <span className="flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Przetwarzanie płatności...
                        </span>
                        {isBlikPayment && (
                          <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted p-2 rounded">
                            <Smartphone className="h-4 w-4" />
                            Potwierdź płatność w swojej aplikacji bankowej
                          </div>
                        )}
                      </div>
                    ) : (
                      'Kup więcej kredytów'
                    )}
                  </h3>
                  <div className="grid grid-cols-3 gap-2">
                    {/* Pakiety z jednej stalej — regulamin wymienia dokladnie te same (terms-page). */}
                    {CREDIT_PACKAGES.map((credits) => (
                      <Button
                        key={credits}
                        variant="outline"
                        data-testid={`buy-credits-${credits}`}
                        onClick={() => handleBuyCredits(credits)}
                        className="w-full h-[72px] flex flex-col items-center justify-center"
                        disabled={isPendingTransaction}
                      >
                        <span className="text-xl">+{credits}</span>
                        <span className="text-sm">{creditsToPln(credits)} PLN</span>
                      </Button>
                    ))}
                  </div>
                </div>
              ) : (
                <TopupOffer onNavigate={onClose} />
              )}

              <div className="mt-8 space-y-4 pt-4 border-t">
                {inviteLink && <div className="space-y-2">
                  <h3 className="text-sm font-medium text-muted-foreground">
                    Link polecający
                  </h3>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 p-2 bg-muted rounded text-xs break-all">
                      {inviteLink}
                    </code>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handleCopyInviteLink}
                      className="shrink-0"
                    >
                      {isCopied ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  {/* Liczby z tych samych stalych, ktore przyznaje handle_new_user — tekst
                      obiecywal 100 i 50, gdy baza dawala 25 i +50 (TASK-88). */}
                  <p className="text-xs text-muted-foreground" data-testid="invite-hint">
                    {t('invite-hint', { invited: INVITED_CREDITS, bonus: INVITER_BONUS_CREDITS })}
                  </p>
                </div>}

                <Button
                  variant="ghost"
                  className="w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={() => setShowLogoutConfirm(true)}
                  data-testid="logout-button"
                  disabled={isPendingTransaction}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Wyloguj się
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {paymentsEnabled && (
          <ConfirmPurchaseDialog
            isOpen={purchaseCreditsAmount !== null}
            onClose={() => setPurchaseCreditsAmount(null)}
            onConfirm={handleConfirmPurchase}
            amount={purchaseCreditsAmount ?? 0}
            isLoading={isLoading}
          />
        )}

        <LogoutConfirmDialog
          isOpen={showLogoutConfirm}
          onClose={() => setShowLogoutConfirm(false)}
          onConfirm={handleLogout}
        />
      </>
    )
} 
